/**
 * Plain-language error text for chamber staff (wave 1, Oct 19 2026).
 *
 * Screens must never show raw `error.message`: an ApolloError message carries
 * wrapper text ("Network error: Failed to fetch") and client guard errors carry
 * internal words. Use `getUserFacingErrorText(error, fallback)` instead.
 */

/** Support address that chamber staff see in the staff dashboard. */
export const STAFF_SUPPORT_EMAIL = "moeen@onechambersystem.com";

export const NETWORK_ERROR_TEXT =
  "We could not reach Chamber Member Pro. Check your internet connection and try again.";

/**
 * cap-backend replaces technical server text with this sentence. The screen
 * then shows the caller's task-specific fallback, which says more.
 */
export const BACKEND_GENERIC_USER_ERROR_MESSAGE =
  "We could not finish this request. Refresh the page and try again.";

/**
 * Builds the standard staff fallback: "We could not [task]. Try again. If it
 * keeps happening, email moeen@onechambersystem.com."
 * `task` starts with a verb in lower case, for example "save the invoice".
 * `detail` is an optional sentence placed after the first one, for example
 * what did not change. Use it only when it is true.
 */
export const buildStaffErrorFallback = (task: string, detail?: string): string =>
  [
    `We could not ${task.trim().replace(/\.$/, "")}.`,
    detail?.trim(),
    `Try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`,
  ]
    .filter(Boolean)
    .join(" ");

/**
 * Fallback for a save whose result is not known (it may have saved). Staff
 * check first, so they do not record a payment or send an email twice:
 * "We could not [task]. [checkStep] before you try again. If it keeps
 * happening, email moeen@onechambersystem.com."
 * `checkStep` example: "Refresh the page and check the invoice status".
 */
export const buildStaffCheckFirstFallback = (
  task: string,
  checkStep: string
): string =>
  `We could not ${task.trim().replace(/\.$/, "")}. ${checkStep
    .trim()
    .replace(/\.$/, "")} before you try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;

/**
 * A client error made on purpose with text that staff can read and act on.
 * `getUserFacingErrorText` shows its message. Internal guard errors stay
 * plain `Error` so that staff see the caller's fallback instead.
 */
export class UserFacingError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = "UserFacingError";
    if (options && "cause" in options) {
      (this as { cause?: unknown }).cause = options.cause;
    }
    Object.setPrototypeOf(this, UserFacingError.prototype);
  }
}

export const isUserFacingError = (error: unknown): error is UserFacingError =>
  error instanceof UserFacingError ||
  (typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "UserFacingError" &&
    typeof (error as { message?: unknown }).message === "string");

type GraphQLErrorLike = {
  message?: unknown;
  extensions?: Record<string, unknown> | null;
};

type ApolloErrorLike = {
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike>;
  networkError?: unknown;
  clientErrors?: ReadonlyArray<unknown>;
  protocolErrors?: ReadonlyArray<unknown>;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/** True for an ApolloError (or an object with the same shape). */
export const isApolloErrorLike = (error: unknown): error is ApolloErrorLike =>
  isObject(error) &&
  (Array.isArray(error.graphQLErrors) ||
    "networkError" in error ||
    Array.isArray(error.protocolErrors));

/** GraphQL errors of an ApolloError, or the error itself when it is one. */
export const getGraphQLErrors = (
  error: unknown
): ReadonlyArray<GraphQLErrorLike> => {
  if (!isObject(error)) {
    return [];
  }

  if (Array.isArray(error.graphQLErrors)) {
    return error.graphQLErrors as ReadonlyArray<GraphQLErrorLike>;
  }

  if (isObject(error.extensions) && typeof error.message === "string") {
    return [error as GraphQLErrorLike];
  }

  return [];
};

const getStringExtension = (
  error: unknown,
  extensionName: string
): string | undefined => {
  for (const graphQLError of getGraphQLErrors(error)) {
    const value = graphQLError.extensions?.[extensionName];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
};

/** First `extensions.code` of the GraphQL errors, if any. */
export const getGraphQLErrorCode = (error: unknown): string | undefined =>
  getStringExtension(error, "code");

/** First `extensions.cmpErrorReason` of the GraphQL errors, if any. */
export const getCmpErrorReason = (error: unknown): string | undefined =>
  getStringExtension(error, "cmpErrorReason");

export const hasGraphQLErrorCode = (error: unknown, code: string): boolean =>
  getGraphQLErrors(error).some(
    (graphQLError) => graphQLError.extensions?.code === code
  );

/** First non-empty GraphQL error message (server text, no Apollo wrapper). */
export const getGraphQLErrorMessage = (error: unknown): string | undefined => {
  for (const graphQLError of getGraphQLErrors(error)) {
    if (typeof graphQLError.message === "string" && graphQLError.message.trim()) {
      return graphQLError.message.trim();
    }
  }

  return undefined;
};

const FETCH_FAILURE_PATTERN =
  /failed to fetch|load failed|networkerror|network request failed|response not successful/i;

const isFetchFailure = (error: unknown): boolean =>
  isObject(error) &&
  (error.name === "AbortError" ||
    (error instanceof TypeError &&
      FETCH_FAILURE_PATTERN.test(String(error.message || ""))));

/**
 * Text to show to chamber staff for any caught error.
 *
 * - ApolloError with a GraphQL error: that server message, except an empty
 *   message or the backend generic text (then `fallback`).
 * - ApolloError with no GraphQL error (network, "Response not successful"),
 *   or a failed fetch: the network text.
 * - `UserFacingError`: its message.
 * - Anything else (client guard text, JS errors, unknown): `fallback`.
 *
 * Never returns the ApolloError wrapper text. Sentry reporting is not changed.
 */
export const getUserFacingErrorText = (
  error: unknown,
  fallback: string
): string => {
  if (isUserFacingError(error)) {
    return error.message.trim() || fallback;
  }

  const graphQLErrors = getGraphQLErrors(error);
  if (graphQLErrors.length) {
    const message = getGraphQLErrorMessage(error);
    if (!message || message === BACKEND_GENERIC_USER_ERROR_MESSAGE) {
      return fallback;
    }
    return message;
  }

  if (isApolloErrorLike(error) || isFetchFailure(error)) {
    return NETWORK_ERROR_TEXT;
  }

  return fallback;
};
